import React, {useContext, useLayoutEffect} from 'react';

import type {CalculatorTier} from 'client/Assignments/constants';
import {useAnnotationModeStore} from 'client/components/PracticeView/Annotations/store/AnnotationModeStore';
import {NOTES_PANEL_WIDTH_PX} from 'client/components/PracticeView/Annotations/AnnotationNotesPanel/AnnotationNotesPanel';
import {MobilePracticeViewContext} from 'client/components/PracticeView/MobilePracticeViewProvider.context';

import CalculatorWindow from './CalculatorWindow.react';
import {useCalculatorWindowStore} from './calculatorWindow.store';

interface PracticeCalculatorProps {
  /** The most capable calculator the current question allows; `none` hides it. */
  tier: CalculatorTier;
  /** Changes with each question, which gives the student a fresh calculator. */
  resetKey: string | undefined;
  /** Closes the window but keeps its work, for a screen that covers the question. */
  isHidden?: boolean;
  /** Room the page needs beside a docked rail, not counting the notes panel. */
  pageMinWidth?: number;
  /** Overrides the practice view's mobile context, for hosts rendered outside it. */
  isMobile?: boolean;
}

/**
 * The calculator window as a practice view hosts it: tells the window, and the toolbar toggle that
 * shares its store, what the current question allows.
 */
export default function PracticeCalculator({
  tier,
  resetKey,
  isHidden,
  pageMinWidth,
  isMobile
}: PracticeCalculatorProps) {
  const reset = useCalculatorWindowStore((state) => state.reset);
  const setTier = useCalculatorWindowStore((state) => state.setTier);
  const close = useCalculatorWindowStore((state) => state.close);
  const isNotesPanelOpen = useAnnotationModeStore((state) => state.isNotesPanelOpen);
  const {isMobile: isMobilePracticeView} = useContext(MobilePracticeViewContext);

  // Layout effects so a new question never paints with the previous question's calculator. The
  // tier goes first, so a fresh calculator opens on what the new question allows.
  useLayoutEffect(() => {
    setTier(tier);
  }, [setTier, tier]);

  useLayoutEffect(() => {
    reset();
  }, [reset, resetKey]);

  useLayoutEffect(() => {
    if (isHidden) {
      close();
    }
  }, [close, isHidden]);

  useLayoutEffect(
    () => () => {
      reset();
      setTier('none');
    },
    [reset, setTier]
  );

  return (
    <CalculatorWindow
      hostMinWidth={
        pageMinWidth === undefined
          ? undefined
          : pageMinWidth + (isNotesPanelOpen ? NOTES_PANEL_WIDTH_PX : 0)
      }
      isMobile={isMobile ?? isMobilePracticeView}
    />
  );
}
